import { useState } from 'react';
import ConfirmModal from './ConfirmModal';
import Icon from './Icon';
import AmberCallout from './AmberCallout';
import { toast } from './Toast';
import { documentsApi } from '../utils/api';
import { track, EVENTS } from '../utils/analytics';
import {
  DELETION_REASONS, DELETION_REASON_NOTE_MAX, DELETION_RETENTION_DAYS, isDeletionReasonComplete,
} from '../utils/deletionReasons';

// The one delete dialog for a document (DraftsPage's מחק and the document
// page's מחק מסמך). A delete is a SOFT delete now (server/lib/softDelete.js):
// the document leaves every screen at once, waits 30 days in the Command
// Center's מחוקים list where an admin can restore it, then is purged.
//
// The reason is required, as one tap on a chip — the same chips as closing a
// credit, for the same reason: a mandatory text box on a repeated action
// collects "." The note is required only for אחר, and the button stays
// disabled until the answer is complete, so the server's 400 is never the
// first thing anyone hears about it.
//
// `review` builds the page's { opened_at, review_seconds, entry } (utils/
// reviewTiming.js) — a function, read at the confirm so the seconds include
// the time spent choosing the reason — sent so the review log can time it.
// `context` is an optional sentence the caller knows and the dialog doesn't —
// the supplier list says how much was paid on the document.
// `initialReason` pre-picks a chip when the caller already knows why — the
// duplicate compare screen's "זה כפיל · מחק" is that answer (#234). The chip
// stays changeable.
// `message` replaces the generic "למחוק את המסמך ...?" and `docLabel` names
// the document in the closing sentence, for a caller deleting something other
// than the page's own document (the compare screen's "השאר את הטיוטה · מחק את
// הקיים": the draft beside it shares the number). `warning` is what else
// leaves with the document (payments, links) — an amber callout above the
// reason, not a line in the grey box.
// `announce={false}` leaves the success toast to the caller, which gets its
// text as `onDeleted({ notice })`: the review queue shows one toast only
// once it knows where it lands (#303).
// `onMove`, when given (the document page, for an admin; #304, #361): picking לא שייך
// למסעדה הזו offers moving it instead — the caller closes this dialog and
// opens the move dialog. Deleting stays possible (a restaurant that is not a
// customer at all).
export default function DeleteDocumentDialog({
  doc, title, confirmLabel, successMessage, context = '', message = '', docLabel = 'המסמך', warning = null,
  review = null, initialReason = '', announce = true, onDeleted, onCancel, onMove = null,
}) {
  const [reason, setReason] = useState(initialReason);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = isDeletionReasonComplete(reason, note) && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await documentsApi.delete(doc.id, {
        reason,
        ...(note.trim() ? { reason_note: note.trim() } : {}),
        ...(review ? { review: review() } : {}),
      });
      const deleted = res.data?.deleted || {};
      track(EVENTS.DOCUMENT_DELETED, {
        reason,
        doc_type: doc.doc_type,
        was_draft: deleted.was_draft ?? doc.status === 'טיוטה',
        source: deleted.source ?? null,
      });
      const notice = `${successMessage} · אפשר לשחזר ממרכז הבקרה במשך ${DELETION_RETENTION_DAYS} יום`;
      if (announce) toast(notice);
      onDeleted?.({ notice });
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה במחיקה', 'error');
      setBusy(false);
    }
  };

  return (
    <ConfirmModal
      title={title}
      message={message || `למחוק את ${doc.status === 'טיוטה' ? 'הטיוטה' : 'המסמך'} "${doc.doc_type}${doc.doc_num ? ' · ' + doc.doc_num : ''}"?`}
      detail={`${context ? `${context} ` : ''}${docLabel} יירד מכל המסכים. אפשר לשחזר אותו ממרכז הבקרה במשך ${DELETION_RETENTION_DAYS} יום, ואחר כך הוא נמחק לצמיתות.`}
      confirmLabel={busy ? 'מוחק...' : confirmLabel}
      tone="danger"
      confirmDisabled={!ready}
      // The dialog closes only on success (onDeleted) — a failed delete keeps
      // the chosen reason on screen for another try.
      onConfirm={submit}
      onCancel={onCancel}
    >
      {warning && <AmberCallout>{warning}</AmberCallout>}
      <div className="form-group" style={{ marginTop: warning ? 14 : 4 }}>
        <label>סיבת המחיקה (חובה)</label>
        <div className="reason-chips" role="radiogroup" aria-label="סיבת המחיקה">
          {DELETION_REASONS.map(r => {
            const picked = reason === r.key;
            return (
              <button
                key={r.key}
                type="button"
                role="radio"
                aria-checked={picked}
                className={`reason-chip${picked ? ' is-picked' : ''}`}
                onClick={() => setReason(r.key)}
              >
                {picked && <Icon name="check" size={14} strokeWidth={2} />}
                {r.label}
              </button>
            );
          })}
        </div>
        <textarea
          className="form-control"
          style={{ marginTop: 10, minHeight: 56, resize: 'vertical' }}
          placeholder={reason === 'other' ? 'מה הסיבה? (חובה)' : 'הערה (לא חובה)'}
          aria-label="הסבר המחיקה"
          maxLength={DELETION_REASON_NOTE_MAX}
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </div>
      {onMove && reason === 'wrong_restaurant' && (
        <AmberCallout actionLabel="העבר למסעדה" onAction={onMove}>
          המסמך שייך למסעדה אחרת? אפשר להעביר אותו במקום למחוק
        </AmberCallout>
      )}
    </ConfirmModal>
  );
}
